import { z } from 'zod';
import { JobsOnMap, getJobPin, getJobLabel, getJobGroup, getJobCluster } from '../Jobs/JobsOnMap';
import { api, clientSettings } from '../../../stores/api';
import { readonlyMode, scenarioManager } from '../../../stores/store';
import { features } from '../../shared/Features/features';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { MapRoute } from '../../utilities/MapRoute';
import type { Assignment } from './Assignment';
import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';
import { BalloonContentGenerator } from '../Jobs/BalloonContentGenerator';
import { Job } from '../Jobs/Job';
import type { RealisationStatusType } from './AssignmentApi';
import lassoSrc from '../../../lib/shared/Icons/svg/lasso_select_24dp_1F1F1F_FILL0_wght400_GRAD0_opsz24.svg';

interface PlanningOnMapConfig {
    scenarioId?: number | null;
    date?: string;
    assignmentQuery?: any;
    assignmentTypes?: any;
    queries?: Object;
    selectedAssignment?: Assignment | null;
    showRoute?: boolean | string;
    routeId?: string;
    baseRouteId?: string;
    assignOptions?: any;
    getMapObject?: Function;
    selectJobs?: boolean;
    externalJobs?: any[]
}

export class PlanningOnMap extends EventTarget {
    static config: any = {};
    static jobPins: any = {};
    static assignments: any = {};
    static jobsOnMap: JobsOnMap;
    static route: MapRoute | null = null;
    private api: any;
    private apiController: any;
    private map: any;
    private listeners: any = {};
    private rulerStyle: Array<any> = [
        { strokeWeight: '5px', strokeColor: '#617EBC', strokeOpacity: '1', lineCap: 'butt' },
        {
            strokeWeight: '4px',
            strokeColor: '#fff',
            strokeOpacity: '1',
            lineDash: [15, 15],
            lineJoin: 'bevel',
            lineCap: 'butt',
        },
    ];
    private loadingAssignmentsForScenario: number | false = false;
    private jobSelector: any = {
        drawing: false,
        polygon: null,
        label: null,
        labelElement: null,
        jobs: [],
    };
    private jobSelection: number[] = [];
    private jobSelectorSwitch: {on: boolean, element: HTMLDivElement|null} = {
        on: false,
        element: null
    };

    constructor(config: PlanningOnMapConfig) {
        super();
        this.map = Targeo.Base.Map;
        this.api = null;
        this.apiController = api.subscribe(
            function (this: PlanningOnMap, v: any) {
                this.api = v;
            }.bind(this)
        );
        if (PlanningOnMap.jobsOnMap) {
            PlanningOnMap.jobsOnMap.destroy();
        }
        PlanningOnMap.jobsOnMap = new JobsOnMap({
            type: 'assignment_jobs',
            active: false,
            maxPointZoom: 32,
            getMapObject: this.jobGetMapObject.bind(this),
            filter: this.getFilters(config),
        });
        this.updateConfig(config);
    }

    public reload() {
        let oldConfig: PlanningOnMapConfig = {};
        Object.assign(oldConfig, PlanningOnMap.config);
        PlanningOnMap.config = {};
        this.updateConfig(oldConfig);
    }

    public updateConfig(config: PlanningOnMapConfig) {
        let oldConfig: PlanningOnMapConfig = {};
        Object.assign(oldConfig, PlanningOnMap.config);
        PlanningOnMap.config = Object.assign(PlanningOnMap.config, config);
        this.jobsRemove();
        if (this.loadingAssignmentsForScenario !== PlanningOnMap.config.scenarioId) {
            this.dataReload(oldConfig.scenarioId !== PlanningOnMap.config.scenarioId).then(
                function (this: PlanningOnMap) {
                    let cfg = PlanningOnMap.config;
                    if (cfg.scenarioId && !cfg.showRoute && !cfg.externalJobs) {
                        PlanningOnMap.jobsOnMap.setFilter(this.getFilters(cfg));
                        PlanningOnMap.jobsOnMap.activate();
                        this.dispatchEvent(
                            new CustomEvent('filterset', { detail: this.getFilters(cfg) })
                        );
                    } else {
                        PlanningOnMap.jobsOnMap.deactivate();
                        if (cfg.externalJobs) {
                            for (let aj of cfg.externalJobs) {
                                let j = {
                                    assignment_id: aj.assignment_id,
                                    ajId: aj.id,
                                    id: aj.job_id,
                                    geom: aj.job.geom,
                                    doc: aj.job.special_attributes.delivery_or_collection
                                }
                                this.map.UOAdd(this.jobGetMapObject(j));
                            }
                        }
                    }
                }.bind(this)
            );
        }
        if (PlanningOnMap.config.selectedAssignment) {
            if (PlanningOnMap.config.showRoute) {
                this.jobSelectorDestroy();
                if (
                    !oldConfig.selectedAssignment ||
                    PlanningOnMap.config.selectedAssignment.getRouteId() != oldConfig.routeId ||
                    PlanningOnMap.config.showRoute != oldConfig.showRoute
                ) {
                    PlanningOnMap.config.routeId =
                        PlanningOnMap.config.showRoute === true ||
                        (PlanningOnMap.config.showRoute === 'current_in_realisation' &&
                            PlanningOnMap.config.selectedAssignment.getRouteInRealisation())
                            ? (PlanningOnMap.config.selectedAssignment.getRouteInRealisation().id ??
                              null)
                            : PlanningOnMap.config.selectedAssignment.getRouteId();
                    this.routeInit();
                }
            } else {
                this.routeRemove();
                this.jobSelectorInit();
            }
        } else {
            this.jobSelectorInit();
            this.routeRemove();
        }
    }

    public zoomBestFit(toFiltered: boolean = false) {
        PlanningOnMap.jobsOnMap.zoomBestFit(toFiltered);
    }

    public zoomToAssignment(assignmentQuery?: any) {
        let endpoint = 'geom_bounds';
        let filter: any;
        if (assignmentQuery) {
            filter = { ...assignmentQuery };
        } else {
            filter = {
                ...PlanningOnMap.config.assignmentTypes,
                assignment_id: PlanningOnMap.config.selectedAssignment.getId(),
                query: {},
                day: PlanningOnMap.config.date,
            };
            endpoint = 'geom_bounds_in_assignment';
        }
        this.api
            .getData(
                'post',
                '/assignment_jobs/' + endpoint + '?relations[0]=job*',
                filter,
                z.any(),
                z.any()
            )
            .then((assignments: any) => {
                let bounds = null;
                const mergeBounds = function (b1: any, b2: any) {
                    b1.minX = Math.min(b1.minX, b2.minX);
                    b1.minY = Math.min(b1.minY, b2.minY);
                    b1.maxX = Math.max(b1.maxX, b2.maxX);
                    b1.maxY = Math.max(b1.maxY, b2.maxY);
                };
                const geojsonToBounds = function (geom: { type: string; coordinates: number[] }) {
                    let b = null;
                    if (Array.isArray(geom.coordinates[0])) {
                        for (let c of geom.coordinates[0]) {
                            let cb = { minX: c[0], minY: c[1], maxX: c[0], maxY: c[1] };
                            if (b === null) {
                                b = cb;
                            } else {
                                mergeBounds(b, cb);
                            }
                        }
                    }
                    return b;
                };
                for (let i = 0, ii = assignments.length; i < ii; i++) {
                    if (assignments[i].geom) {
                        if (bounds === null) {
                            bounds = geojsonToBounds(assignments[i].geom);
                        } else {
                            mergeBounds(bounds, geojsonToBounds(assignments[i].geom));
                        }
                    }
                }
                if (bounds !== null) {
                    this.map.zoomToBounds(bounds, false, [80, 120, 20, 180]);
                }
            })
            .catch((err: any) => {
                console.error('PlanningOnMap, zoomToAssignment', err);
            });
    }

    private getFilters(cfg: PlanningOnMapConfig): any {
        let filter: any = {};
        let jobsOnMapQueries = JSON.parse(JSON.stringify(cfg.queries));
        if (cfg.assignmentQuery) {
            filter = { ...cfg.assignmentQuery };
            if (Object.keys(jobsOnMapQueries).length) {
                filter['$filter'] = jobsOnMapQueries;
            }
        } else if (cfg.selectedAssignment && cfg.assignmentTypes) {
            filter = {
                ...cfg.assignmentTypes,
                assignment_id: cfg.selectedAssignment.getId(),
                query: {},
                day: cfg.date,
            };
            if (Object.keys(jobsOnMapQueries).length) {
                filter.query['$filter'] = jobsOnMapQueries;
            }
        } else {
            filter = jobsOnMapQueries;
        }
        return filter;
    }

    private dataReload(loadAssignments: boolean): Promise<void> {
        return new Promise((resolve, reject) => {
            if (loadAssignments) {
                this.assignmentsLoad().then(
                    function () {
                        resolve();
                    }.bind(this)
                );
            } else {
                resolve();
            }
        });
    }

    private assignmentsLoad() {
        this.loadingAssignmentsForScenario = PlanningOnMap.config.scenarioId;
        return new Promise((resolve, reject) => {
            this.assignmentsRemove();
            let assignmentFilter = new ApiFilterBuilder(-1);
            assignmentFilter.filterByNumber('scenario_id', PlanningOnMap.config.scenarioId);
            assignmentFilter.setRelations(['zone', 'vehicle', 'tactical_plan_rule']);
            /*this.api
                .getData('get', '/assignments' + assignmentFilter.getUri(), undefined, z.undefined(), z.any())
                .then(
                    function (this: PlanningOnMap, assignments: any) {
                        this.assignmentsOnLoad(assignments);
                        resolve('');
                    }.bind(this)
                )
                .catch((err: any) => {
                    console.error('assignments', err);
                });*/
            let tacticalPlanId = get(scenarioManager).getScenario()?.tactical_plan_id;
            Promise.all([
                this.api.getData(
                    'get',
                    '/assignments' + assignmentFilter.getUri(),
                    undefined,
                    z.undefined(),
                    z.any()
                ),
                tacticalPlanId && get(scenarioManager).getScenario()?.id === PlanningOnMap.config.scenarioId
                    ? this.api.getData(
                          'post',
                          '/tactical_plan/' + tacticalPlanId + '/get_zones',
                          [],
                          z.any(),
                          z.any()
                      )
                    : null,
            ]).then(
                function (this: PlanningOnMap, responses: any[]) {
                    this.assignmentsOnLoad(responses[0], responses[1] ?? null);
                    resolve('');
                }.bind(this)
            );
        });
    }

    private assignmentsOnLoad(assignments: any, zones: any) {
        if (this.loadingAssignmentsForScenario) {
            this.map.getObjectManager().disableRefreshing('LastMile:PlanningOnMap');
            for (let i = 0, ii = assignments.length; i < ii; i++) {
                let a = assignments[i];
                PlanningOnMap.assignments[a.id] = {
                    id: a.id,
                    color: a.color,
                    vehicleId: a.vehicle_id,
                    vehicleName: a.vehicle?.name || null,
                };
                if (a.zone?.id) {
                    PlanningOnMap.assignments[a.id].zoneId = a.zone.id;
                }
                if (
                    a.zone && a.tactical_plan_rule && (a.tactical_plan_rule.tactical_plan_id == get(scenarioManager).getScenario()?.tactical_plan_id || get(scenarioManager).getScenario()?.id !== PlanningOnMap.config.scenarioId)
                ) {
                    let style = [
                        {
                            strokeWeight: 3,
                            strokeOpacity: 0.8,
                            fillOpacity: 0.2,
                            strokeColor: a.color,
                            fill: a.color,
                        },
                    ];
                    PlanningOnMap.assignments[a.id].mapObjects = [
                        new Targeo.MapObject.Polyline(
                            a.zone.geom.coordinates,
                            {
                                closed: true,
                                visible: true,
                                drawStyle: style,
                                mouseButtonActions: {
                                    left: function (this: PlanningOnMap, mapObj: any) {
                                        let assignmentId = mapObj.id.split('_')[1];
                                        this.dispatchEvent(
                                            new CustomEvent('zoneclick', {
                                                detail: { assignmentId: assignmentId },
                                            })
                                        );
                                    }.bind(this),
                                },
                            },
                            'assignment_' + a.id + '_' + a.zone.id
                        ),
                    ];
                    try {
                        this.map.UOAdd(PlanningOnMap.assignments[a.id].mapObjects[0]);
                    } catch (e) {
                        // https://er.automapa.pl/issues/44724
                        console.warn('EXCEPTION: PlanningOnMap, assignmentsOnLoad, UOAdd', e);
                    }
                }
            }
            if (zones) {
                for (let i = 0, ii = zones.length; i < ii; i++) {
                    let zone = zones[i];
                    if (
                        zone.vehicle_id &&
                        Object.values(PlanningOnMap.assignments).filter(
                            (a: any) => a.zoneId === zone.id
                        ).length === 0
                    ) {
                        let assignmentByVehicle:any =
                            Object.values(PlanningOnMap.assignments).filter(
                                (a: any) => a.vehicleId === zone.vehicle_id
                            )[0] ?? null;
                        if (assignmentByVehicle) {
                            let style = [
                                {
                                    strokeWeight: 3,
                                    strokeOpacity: 0.8,
                                    fillOpacity: 0.2,
                                    strokeColor: assignmentByVehicle.color,
                                    fill: assignmentByVehicle.color,
                                },
                            ];
                            let mapObj = new Targeo.MapObject.Polyline(
                                zone.geom.coordinates,
                                {
                                    closed: true,
                                    visible: true,
                                    drawStyle: style,
                                    mouseButtonActions: {
                                        left: function (this: PlanningOnMap, mapObj: any) {
                                            let assignmentId = mapObj.id.split('_')[1];
                                            this.dispatchEvent(
                                                new CustomEvent('zoneclick', {
                                                    detail: { assignmentId: assignmentId },
                                                })
                                            );
                                        }.bind(this),
                                    },
                                },
                                'assignment_' + assignmentByVehicle.id + '_' + zone.id
                            );
                            if (
                                !('mapObjects' in PlanningOnMap.assignments[assignmentByVehicle.id])
                            ) {
                                PlanningOnMap.assignments[assignmentByVehicle.id].mapObjects = [];
                            }
                            PlanningOnMap.assignments[assignmentByVehicle.id].mapObjects.push(
                                mapObj
                            );
                            this.map.UOAdd(mapObj);
                        }
                    }
                }
            }
            this.map.getObjectManager().enableRefreshing('LastMile:PlanningOnMap');
            this.loadingAssignmentsForScenario = false;
        }
    }

    private assignmentsRemove() {
        this.map.getObjectManager().disableRefreshing('LastMile:PlanningOnMap');
        for (let a in PlanningOnMap.assignments) {
            if (PlanningOnMap.assignments[a].mapObjects?.length) {
                PlanningOnMap.assignments[a].mapObjects.forEach(
                    function (this: PlanningOnMap, mapObj: any) {
                        this.map.UORemove(mapObj.id);
                    }.bind(this)
                );
            }
        }
        this.map.getObjectManager().enableRefreshing('LastMile:PlanningOnMap');
        PlanningOnMap.assignments = {};
    }

    private jobsReload() {}

    private jobsRemove() {
        this.map.getObjectManager().disableRefreshing('LastMile:PlanningOnMap');
        PlanningOnMap.jobsOnMap.jobsRemove();
        for (let j in PlanningOnMap.jobPins) {
            if (PlanningOnMap.jobPins[j].mapObject) {
                this.map.UORemove(PlanningOnMap.jobPins[j].mapObject.id);
            }
        }
        this.map.getObjectManager().enableRefreshing('LastMile:PlanningOnMap');
        PlanningOnMap.jobPins = {};
    }

    private jobGetMapObject(job: any) {
        let mapObj: any;
        if ((job.count && job.count > 1) || !(job.id in PlanningOnMap.jobPins)) {
            let color = '#ffffff';
            let pinOpts: any = {};
            if (
                job.assignment_id &&
                PlanningOnMap.assignments[job.assignment_id.toString()] &&
                PlanningOnMap.assignments[job.assignment_id.toString()].color
            ) {
                color = PlanningOnMap.assignments[job.assignment_id.toString()].color;
            } else if (
                job.suggested_assignment_id &&
                PlanningOnMap.assignments[job.suggested_assignment_id.toString()] &&
                PlanningOnMap.assignments[job.suggested_assignment_id.toString()].color
            ) {
                color = PlanningOnMap.assignments[job.suggested_assignment_id.toString()].color;
                pinOpts.colorOpacity = 0.6;
            } else if (!job.assignment_id) {
                color = '#f9e196';
                pinOpts.backgroundColor = '#d93b4d';
                pinOpts.borderColor = '#ff0000';
            }
            let visible =
                !job.assignment_id ||
                !PlanningOnMap.config.selectedAssignment ||
                !PlanningOnMap.config.showRoute ||
                !PlanningOnMap.config.selectedAssignment.getRouteId() ||
                job.assignment_id != PlanningOnMap.config.selectedAssignment.getId();
            let zIndex = 502 + 90 - job.geom.coordinates[1]; // Make icons place like for real
            if (job.count && job.count > 1) {
                mapObj = new Targeo.MapObject.Point(
                    { x: job.geom.coordinates[0], y: job.geom.coordinates[1] },
                    getJobGroup(
                        color,
                        30 + (job.count.toString().length - 1) * 10,
                        Object.assign(pinOpts, { total: job.count, filtered: job.filter })
                    ),
                    null,
                    { visible: visible, zIndex: zIndex }
                );
            } else {
                let selected = this.jobSelection.includes(job.id);
                let draggable = false;
                if (!PlanningOnMap.config.showRoute) {
                    if (!job.assignment_id) {
                        draggable = true;
                    } else {
                        let jobsAssignment = get(scenarioManager).getScenarioSections(false).filter(a => a.getId() == job.assignment_id)[0];
                        if (jobsAssignment && !jobsAssignment.isLocked() && !jobsAssignment.hasRouteBeenEverDownloaded()) {
                            draggable = true;
                        }
                    }
                }
                let cluster = getJobCluster(
                    color,
                    /*job.filter === false
                        ? JobsOnMap.pinSizes.unmatching
                        : job.filter === true
                          ? JobsOnMap.pinSizes.matching
                          : JobsOnMap.pinSizes.normal,*/
                    JobsOnMap.pinSizes.normal,
                    Object.assign(pinOpts, { type: job.doc }),
                    get(clientSettings).display.icon_style
                );
                mapObj = new Targeo.MapObject.Point(
                    { x: job.geom.coordinates[0], y: job.geom.coordinates[1] },
                    getJobPin(
                        color,
                        job.filter === false
                            ? JobsOnMap.pinSizes.unmatching
                            : job.filter === true
                              ? JobsOnMap.pinSizes.matching
                              : JobsOnMap.pinSizes.normal,
                        Object.assign(pinOpts, { type: job.doc, checked: selected }),
                        get(clientSettings).display.icon_style
                    ),
                    'job_' + job.id + '_' + job.ajId,
                    {
                        name: getJobLabel(job),
                        zIndex: zIndex,
                        visible: visible,
                        balloon: {
                            width: 410,
                            body: '<div class="lds-dual-ring-improved" style="width: 60px; height: 60px;"></div>',
                        },
                        mouseButtonActions: {
                            left: this.jobClick.bind(this, 'left'),
                            right: this.jobClick.bind(this, 'right'),
                        },
                        fGetContextMenu: null, // this.jobContextMenu.bind(this),
                        cluster: cluster,
                        draggable: draggable
                    }
                );
                PlanningOnMap.jobPins[job.id] = {
                    assignmentId: job.assignment_id,
                    suggestedAssignmentId: job.suggested_assignment_id,
                    ajId: job.ajId,
                    type: job.doc,
                    filter: job.filter,
                    ad_hoc: job.ad_hoc,
                    in_realisation: job.rl,
                    mapObject: mapObj,
                    geom: job.geom,
                    detailsLoaded: false,
                    balloonListener: null,
                    color: color,
                    pinOpts: pinOpts,
                    cluster: cluster,
                    selected: selected
                };
                if (draggable) {
                    mapObj.listen('onDrag', (function(this: PlanningOnMap, e: any) {
                        let jobId = parseInt(e.target.id.split('_')[1]);
                        let jobData = PlanningOnMap.jobPins[jobId];
                        this.jobSelector.jobs = [{jobId: jobId, ajId: jobData.ajId, assignmentId: jobData.assignmentId}];
                        this.jobDragOnDrag({...e, objects: [e.target]});
                    }).bind(this));
                    mapObj.listen('onDragEnd', (function(this: PlanningOnMap, e: any) {
                        this.jobDragOnDragEnd({...e, objects: [e.target]});
                    }).bind(this));
                }
            }
        } else {
            mapObj = PlanningOnMap.jobPins[job.id].mapObject;
        }
        if (PlanningOnMap.jobPins[job.id] && !PlanningOnMap.jobPins[job.id].detailsLoaded) {
            if (PlanningOnMap.jobPins[job.id].balloonListener) {
                PlanningOnMap.jobPins[job.id].mapObject.unlistenByKey(
                    PlanningOnMap.jobPins[job.id].balloonListener
                );
            }
            PlanningOnMap.jobPins[job.id].balloonListener = mapObj.listen(
                'onBalloonShow',
                function (this: PlanningOnMap, ev: any) {
                    var jobId = parseInt(ev.target.id.split('_')[1]);
                    if (PlanningOnMap.jobPins[job.id]) {
                        if (1 || !PlanningOnMap.jobPins[job.id].detailsLoaded) {
                            let filter = new ApiFilterBuilder(-1);
                            filter.setRelations(['assignment_job', 'assignment_job_rejection']);
                            filter.setProjectionFields([
                                'job.*',
                                'geom',
                                'assignment_job>details@realisation',
                                'assignment_job_rejection>driver_info@rejection',
                            ]);
                            let queries = {
                                'job.id': jobId,
                                $or: [
                                    { rejected: false },
                                    {
                                        'rejected_assignments.-1::int': {
                                            $column: 'assignment_job_rejection.assignment_id',
                                        },
                                    },
                                ],
                            };
                            this.api
                                .getData(
                                    'post',
                                    '/job/query' + filter.getUri(),
                                    queries,
                                    z.any(),
                                    z.any()
                                )
                                .then(
                                    function (this: PlanningOnMap, jobs: any[]) {
                                        let job = jobs[0] ?? null;
                                        if (job && PlanningOnMap.jobPins[job.id] !== undefined) {
                                            let balloonContentGenerator =
                                                new BalloonContentGenerator(new Job().set(job));
                                            let html =
                                                balloonContentGenerator.getBalloonHtmlArray();
                                            PlanningOnMap.jobPins[
                                                job.id
                                            ].mapObject.setBalloonOptions({
                                                body: html.join(''),
                                            });
                                            this.map
                                                .getObjectManager()
                                                .refreshBalloon(ev.target.id);
                                            PlanningOnMap.jobPins[job.id].detailsLoaded = job;
                                            balloonContentGenerator.connectBalloonThumbnails();
                                        }
                                    }.bind(this)
                                )
                                .catch();
                        } else {
                            let balloonContentGenerator = new BalloonContentGenerator(
                                new Job().set(PlanningOnMap.jobPins[job.id].detailsLoaded)
                            );
                            balloonContentGenerator.connectBalloonThumbnails();
                        }
                    }
                }.bind(this)
            );
        }
        return mapObj;
    }

    jobContextMenu(mapObjId: any) {
        let opts: any[] = [];
        if (
            PlanningOnMap.config.assignOptions &&
            Object.keys(PlanningOnMap.config.assignOptions).length &&
            PlanningOnMap.config.selectedAssignment
        ) {
            let jobId = parseInt(mapObjId.split('_')[1]);
            let props = PlanningOnMap.jobPins[jobId];
            let assigned =
                props.assignmentId &&
                PlanningOnMap.config.selectedAssignment.getId() == props.assignmentId;
            let label = PlanningOnMap.config.assignOptions[assigned ? 'unassign' : 'assign'];
            let sass = PlanningOnMap.config.selectedAssignment;
            if (label) {
                if (
                    assigned &&
                    (sass.isLocked() ||
                        (sass.isRouteModifiedAfterDownload() && props.in_realisation))
                ) {
                    label = false;
                } else if (
                    !assigned &&
                    (((sass.isLocked() || sass.isRouteModifiedAfterDownload()) && !props.ad_hoc) ||
                        (sass.hasRouteBeenEverDownloaded() && props.type !== 'collection'))
                ) {
                    label = false;
                }
            }
            if (label && !assigned) {
                let assignTo = PlanningOnMap.config.selectedAssignment.getId()
                    ? PlanningOnMap.config.selectedAssignment.getId()
                    : props.suggestedAssignmentId;
                if (assignTo && PlanningOnMap.assignments[assignTo]?.vehicleName) {
                    label += ' ' + PlanningOnMap.assignments[assignTo]?.vehicleName;
                } else if (!assignTo) {
                    label = null;
                }
            }
            let icon = (
                `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="10" viewBox="0 0 50 19.685">
                    <line x1="10" y1="5" x2="15" y2="5" stroke="{color}" stroke-width="1.5"/>
                    <line x1="5" y1="9" x2="15" y2="9" stroke="{color}" stroke-width="1.5"/>
                    <line x1="0" y1="13" x2="15" y2="13" stroke="{color}" stroke-width="1.5"/>
                    <path d="M28.75,16.384l-3.029-6.057H21.179V7.3a1.519,1.519,0,0,0-1.514-1.514H.085A1.519,1.519,0,0,0-1.429,7.3V19.412L.085,20.927h1.92a3.029,3.029,0,1,0,5.245,0H21.585a3.029,3.029,0,1,0,5.245,0h1.92V16.384Zm-7.571,0V11.841h3.139l2.271,4.543h-5.41Z" transform="translate(20 -5.784)" fill="{color}"/>
                    ` +
                (assigned
                    ? `<line x1="15" y1="25" x2="35" y2="-5" stroke="#fff" stroke-width="9.5"/><line x1="15" y1="25" x2="35" y2="-5" stroke="{color}" stroke-width="3.5"/>`
                    : ``) +
                `
                </svg>`
            ).replace(/\{color\}/g, assigned ? '#d93b4d' : '#77b024');
            if (label) {
                opts.push({
                    label: label,
                    icon: 'data:image/svg+xml;base64,' + window.btoa(icon),
                    handler: function (this: PlanningOnMap) {
                        if (!PlanningOnMap.jobPins[jobId].clicked) {
                            PlanningOnMap.jobPins[jobId].clicked = true;
                            setTimeout(function () {
                                PlanningOnMap.jobPins[jobId].clicked = false;
                            }, 1000);
                            this.dispatchEvent(
                                new CustomEvent('jobclick', {
                                    detail: {
                                        jobId: jobId,
                                        ajId: props.ajId || null,
                                        assignmentId: props.assignmentId,
                                        suggestedAssignmentId: props.suggestedAssignmentId,
                                        hasSuggestion: !!props.suggestedAssignmentId,
                                    },
                                })
                            );
                        }
                    }.bind(this),
                    active: true,
                });
            }
            if (!assigned && PlanningOnMap.config.selectedAssignment.getId() === 0) {
                opts.push({
                    label: PlanningOnMap.config.assignOptions.assign + '...',
                    icon: 'data:image/svg+xml;base64,' + window.btoa(icon),
                    handler: function (this: PlanningOnMap) {
                        if (!PlanningOnMap.jobPins[jobId].clicked) {
                            PlanningOnMap.jobPins[jobId].clicked = true;
                            setTimeout(function () {
                                PlanningOnMap.jobPins[jobId].clicked = false;
                            }, 1000);
                            this.dispatchEvent(
                                new CustomEvent('jobclick', {
                                    detail: {
                                        jobId: jobId,
                                        ajId: props.ajId || null,
                                        assignmentId: props.assignmentId,
                                        suggestedAssignmentId: null,
                                        hasSuggestion: !!props.suggestedAssignmentId,
                                    },
                                })
                            );
                        }
                    }.bind(this),
                    active: true,
                });
            }
        }
        console.log('jobContextMenu', mapObjId, opts);
        return opts;
    }

    jobShowBalloon(jobId: number) {
        if (PlanningOnMap.route) {
            PlanningOnMap.route.showBalloonByJob(jobId);
        } else {
            PlanningOnMap.jobsOnMap.jobShowBalloon(jobId);
        }
    }

    jobClick(button: string, mapObj: any, evt: any) {
        let id = mapObj.id.split('_');
        let jobId = parseInt(id[1]);
        let props = PlanningOnMap.jobPins[jobId];
        if (props.filter !== false && !PlanningOnMap.jobPins[jobId].clicked) {
            PlanningOnMap.jobPins[jobId].clicked = true;
            setTimeout(function () {
                PlanningOnMap.jobPins[jobId].clicked = false;
            }, 100);
            // when user click on pin
            let jobcoords = proj4('EPSG:4326', 'EPSG:3857', [mapObj.coords.x, mapObj.coords.y]);
            let mapcorner = proj4('EPSG:4326', 'EPSG:3857', [
                this.map.getVisibleExtent().minX,
                this.map.getVisibleExtent().maxY,
            ]);
            let maprect = this.map.getContainer().getBoundingClientRect();
            let mapresolution = this.map.getResolution();
            let page = [jobcoords[0] - mapcorner[0], mapcorner[1] - jobcoords[1]].map(
                d => d / mapresolution
            );
            page[0] += maprect.left;
            page[1] += maprect.top;
            let eventName;
            if (button === 'left') {
                if (PlanningOnMap.config.selectJobs) {
                    let selected = this.jobSelection.includes(jobId);
                    if (evt.ctrlKey) {
                        if (selected) {
                            selected = false;
                            this.jobSelection.splice(this.jobSelection.indexOf(jobId), 1);
                        } else {
                            selected = true;
                            this.jobSelection.push(jobId);
                        }
                    } else {
                        selected = !selected || this.jobSelection.length != 1;
                        this.jobSelection.forEach((j) => {
                            if (!selected || jobId != j) {
                                this.jobSelect(j, false);
                            }
                        });
                        this.jobSelection = selected ? [jobId] : [];
                    }
                    eventName = 'jobselect';
                    this.jobSelect(jobId, selected);
                    this.jobSetSelection(this.jobSelection, true);
                } else {
                    eventName = 'jobclick';
                }
            } else {
                eventName = 'jobrightclick';
            }
            this.dispatchEvent(
                new CustomEvent(eventName, {
                    detail: {
                        jobId: jobId,
                        ajId: props.ajId || null,
                        assignmentId: props.assignmentId,
                        suggestedAssignmentId: props.suggestedAssignmentId,
                        hasSuggestion: !!props.suggestedAssignmentId,
                        isSelected: props.selected,
                        selection: this.jobSelection,
                        selectionDetailed: this.jobSelection.map((j) => {
                            return {...(
                                ({
                                    ajId: id,
                                    assignmentId: assignment_id,
                                    suggestedAssignmentId: suggested_assignment_id
                                }) => ({id, assignment_id, suggested_assignment_id}))(PlanningOnMap.jobPins[j] || {}), job_id: j};
                        }),
                        page: page,
                    },
                })
            );
        }
    }

    jobSelect(jobId: number, selected: boolean) {
        let props = PlanningOnMap.jobPins[jobId];
        if (props && props.selected != selected) {
            props.selected = selected;
            if (props.mapObject) {
                props.mapObject.setIcon(
                    getJobPin(
                        props.color,
                        props.filter === false
                            ? JobsOnMap.pinSizes.unmatching
                            : props.filter === true
                            ? JobsOnMap.pinSizes.matching
                            : JobsOnMap.pinSizes.normal,
                        { ...props.pinOpts, type: props.type, checked: props.selected },
                        get(clientSettings).display.icon_style
                    )
                );
            }
        }
    }

    jobSetSelection(selection: number[], changed: boolean = false) {
        console.log('PlanningOnMap, jobSetSelection', selection.join(','), this.jobSelection.join(','), changed);
        if (selection !== this.jobSelection) {
            this.jobSelection.forEach((j) => {
                if (!selection.includes(j)) {
                    this.jobSelect(j, false);
                    changed = true;
                }
            });
            selection.forEach((j) => {
                if (!this.jobSelection.includes(j)) {
                    this.jobSelect(j, true);
                    changed = true;
                }
            });
            this.jobSelection = selection;
        }
        if (changed && this.jobSelector) {
            setTimeout((function(this: PlanningOnMap) {
                if (this.jobSelector.polygon) {
                    let removePolygon = true;
                    let jobsInsidePolygon = this.jobSelector.jobsInsidePolygon ? this.jobSelector.jobsInsidePolygon.map(j => j.jobId) : [];
                    if (jobsInsidePolygon.length == this.jobSelection.length && jobsInsidePolygon.filter(j => !this.jobSelection.includes(j)).length == 0) {
                        removePolygon = false;
                    }
                    if (removePolygon) {
                        this.map.UORemove(this.jobSelector.polygon.id);
                        this.jobSelector.polygon = null;
                    }
                }
                this.jobDragClean(true);
                this.map.getObjectManager().destroyDraggableGroup();
                if (this.jobSelection.length > 1) {
                    this.jobSelectorMark(null, this.jobSelection.filter(j => !!PlanningOnMap.jobPins[j]?.mapObject).map((j) => { return {jobId: j, assignmentId: PlanningOnMap.jobPins[j].assignmentId}; }));
                }
            }).bind(this), 0);
        }
    }

    jobReassign(jobId: number, assignmentId: number, ajId: number | null) {
        if (PlanningOnMap.jobPins[jobId]) {
            let jobData = PlanningOnMap.jobPins[jobId];
            jobData.assignmentId = assignmentId;
            jobData.suggestedAssignmentId = null;
            jobData.ajId = ajId;
            let color = '#ffffff';
            let pinOpts: any = {};
            if (
                jobData.assignmentId &&
                PlanningOnMap.assignments[jobData.assignmentId.toString()] &&
                PlanningOnMap.assignments[jobData.assignmentId.toString()].color
            ) {
                color = PlanningOnMap.assignments[jobData.assignmentId.toString()].color;
            } else if (
                jobData.suggestedAssignmentId &&
                PlanningOnMap.assignments[jobData.suggestedAssignmentId.toString()] &&
                PlanningOnMap.assignments[jobData.suggestedAssignmentId.toString()].color
            ) {
                color = PlanningOnMap.assignments[jobData.suggestedAssignmentId.toString()].color;
                pinOpts.colorOpacity = 0.6;
            } else if (!jobData.assignmentId) {
                color = '#f9e196';
                pinOpts.backgroundColor = '#d93b4d';
                pinOpts.borderColor = '#ff0000';
            }
            PlanningOnMap.jobPins[jobId].mapObject.setIcon(
                getJobPin(
                    color,
                    jobData.filter === false
                        ? JobsOnMap.pinSizes.unmatching
                        : jobData.filter === true
                          ? JobsOnMap.pinSizes.matching
                          : JobsOnMap.pinSizes.normal,
                    { ...pinOpts, type: jobData.type, checked: jobData.selected },
                    get(clientSettings).display.icon_style
                )
            );
            //PlanningOnMap.jobPins[jobId].assignmentId = assignmentId;
            //PlanningOnMap.jobPins[jobId].ajId = ajId;
            /*if (PlanningOnMap.config.assignmentIds.indexOf(assignmentId) == -1) {
                this.map.UOHide(PlanningOnMap.jobPins[jobId].mapObject.id);
            } else {
                this.map.UOShow(PlanningOnMap.jobPins[jobId].mapObject.id);
            }*/
            if (assignmentId) {
                if (PlanningOnMap.jobPins[jobId].clicked) {
                    PlanningOnMap.jobPins[jobId].clicked = false;
                }
            } else {
                PlanningOnMap.jobsOnMap.jobReloadSingle(jobId);
            }
        }
    }

    private routeInit() {
        this.routeRemove();
        let assignmentId = PlanningOnMap.config.selectedAssignment.getId();
        let filter = new ApiFilterBuilder(-1);
        filter.sortBy('point_order', 'asc');
        filter.filterByNumber('assignment_id', PlanningOnMap.config.selectedAssignment.getId());
        this.api
            .getData('get', '/route_points' + filter.getUri(), undefined, z.undefined(), z.any())
            .then(
                ((points: Array<any>) => {
                    if (
                        PlanningOnMap.config.showRoute &&
                        PlanningOnMap.config.selectedAssignment &&
                        PlanningOnMap.config.selectedAssignment.getId() == assignmentId
                    ) {
                        /*let bounds = null;
                const mergeBounds = function(b1: any, b2: any) {
                    b1.minX = Math.min(b1.minX, b2.minX);
                    b1.minY = Math.min(b1.minY, b2.minY);
                    b1.maxX = Math.max(b1.maxX, b2.maxX);
                    b1.maxY = Math.max(b1.maxY, b2.maxY);
                }
                for (let point of points) {
                    let pb = {minX: point.geom.coordinates[0], minY: point.geom.coordinates[1], maxX: point.geom.coordinates[0], maxY: point.geom.coordinates[1]};
                    if (bounds === null) {
                        bounds = pb;
                    } else {
                        mergeBounds(bounds, pb);
                    }
                }
                this.map.zoomToBounds(bounds, false, [65, 125, 25, 125]);*/
                        if (
                            PlanningOnMap.config.showRoute === 'current_in_realisation' &&
                            PlanningOnMap.config.selectedAssignment.getRouteInRealisation()
                        ) {
                            let filteredPoints = [];
                            let pointsInRealisation =
                                PlanningOnMap.config.selectedAssignment.getRouteInRealisation()
                                    .points;
                            for (let i = 0; i < points.length; i++) {
                                if (pointsInRealisation.filter(p => p.id === points[i].id).length) {
                                    filteredPoints.push(points[i]);
                                }
                            }
                            points = filteredPoints;
                        }
                        PlanningOnMap.route = new MapRoute(
                            PlanningOnMap.config.selectedAssignment.getId(),
                            PlanningOnMap.config.routeId,
                            points
                        );
                    }
                }).bind(this)
            );
    }

    public updateRealisationStatus(realisation: RealisationStatusType) {
        if (PlanningOnMap.route) {
            PlanningOnMap.route.updateRealisationStatus(realisation);
        }
    }

    private routeRemove() {
        if (PlanningOnMap.route) {
            PlanningOnMap.route.destroy();
            PlanningOnMap.route = null;
        }
    }

    private jobSelectorInit() {
        this.jobSelectorDestroy();
        this.map.modules.Drawer.drawPrepare({
            type: 'Polygon',
            disableMap: false,
            condition: function (this: PlanningOnMap, evt: any) {
                if (
                    !document.querySelector('.map-header-element') &&
                    (this.jobSelector.drawing || this.jobSelectorSwitch.on ||
                        (navigator?.userAgent?.toLowerCase().indexOf('macintosh') !== -1
                            ? evt.originalEvent?.metaKey
                            : evt.originalEvent?.ctrlKey)) && !get(readonlyMode)
                ) {
                    let mapObjects = this.map.getObjectManager().getObjectsAtPixel(evt);
                    for (let i = 0; i < mapObjects.length; i++) {
                        if (mapObjects[i].id.slice(0, 4) == 'job_') {
                            console.log('jobSelectorInit, condition false');
                            return false;
                        }
                    }
                    console.log('jobSelectorInit, condition true');
                    return true;
                }
                return false;
            }.bind(this),
            drawStyle: [
                {
                    strokeWeight: '2px',
                    strokeColor: '#333333',
                    strokeOpacity: '0.8',
                    fill: '#ffffff77',
                    lineDash: [5, 5],
                },
            ],
        });
        this.listeners.draw = [
            this.map.modules.Drawer.listen('onDrawStart', this.jobSelectorOnDrawStart, false, this),
            this.map.modules.Drawer.listen(
                'onDrawAbort',
                function (this: PlanningOnMap) {
                    this.jobSelector.drawing = false;
                },
                false,
                this
            ),
            this.map.modules.Drawer.listen(
                'onDraw',
                function (this: PlanningOnMap, evt: any) {
                    this.jobSelector.coordinates = evt.coords;
                },
                false,
                this
            ),
            this.map.modules.Drawer.listen(
                'onDrawComplete',
                this.jobSelectorOnDrawComplete,
                false,
                this
            ),
        ];
        if (get(features).feature_ux_improvements) {
            this.jobSelectorSwitch.element = document.createElement('div');
            this.jobSelectorSwitch.element.className = 'targeo-control';
            this.jobSelectorSwitch.element.style.cssText = 'width: 120px; height: 37px; display: flex; justify-content: center; align-items: center; gap: 10px;';
            this.jobSelectorSwitch.element.innerHTML = '<img src="'+lassoSrc+'" style="width: 24px; height: 24px;"/><span>'+get(_)('job_pins_select')+'</span>';
            this.jobSelectorSwitch.element.addEventListener('click', (function(this: PlanningOnMap) {
                if (this.jobSelectorSwitch.element) {
                    let img = this.jobSelectorSwitch.element.querySelector('img');
                    this.jobSelectorSwitch.on = !this.jobSelectorSwitch.on;
                    if (this.jobSelectorSwitch.on) {
                        this.jobSelectorSwitch.element.style.backgroundColor = '#4978e2';
                        this.jobSelectorSwitch.element.style.color = '#ffffff';
                        if (img) {
                            img.style.filter = 'invert(1)';
                        }
                    } else {
                        this.jobSelectorSwitch.element.style.backgroundColor = '';
                        this.jobSelectorSwitch.element.style.color = '';
                        if (img) {
                            img.style.filter = '';
                        }
                    }
                }
            }).bind(this));
            Targeo.Base.Map.modules.Controls.addControl(
                'job-selector',
                this.jobSelectorSwitch.element,
                {
                    align: 'top-right',
                    edge: false,
                    visibilityPriority: 1000,
                    attractPriority: -10,
                }
            );
        }
    }

    private jobSelectorOnDrawStart(evt: any) {
        if (this.jobSelector.polygon) {
            this.map.UORemove(this.jobSelector.polygon.id);
            this.jobSelector.polygon = null;
        }
        if (this.jobSelector.label) {
            this.map.UORemove(this.jobSelector.label.id);
            this.jobSelector.label = null;
        }
        this.jobSelector.drawing = true;
        document.body.removeEventListener('keyup', this.jobSelectorOnKeyUp);
        this.jobSelectorOnKeyUp = this.jobSelectorOnKeyUp.bind(this);
        document.body.addEventListener('keyup', this.jobSelectorOnKeyUp);
    }

    private jobSelectorOnDrawComplete(evt: any) {
        document.body.removeEventListener('keyup', this.jobSelectorOnKeyUp);
        this.jobSelector.drawing = false;
        if (this.jobSelectorSwitch.on && this.jobSelectorSwitch.element) {
            this.jobSelectorSwitch.element.click();
        }
        try {
            if (!document.querySelector('.map-header-element')) {
                let geom = {
                    type: 'Polygon',
                    coordinates: [
                        evt.coords.map((p: { x: number; y: number }) => {
                            return [p.x, p.y];
                        }),
                    ],
                };
                this.jobSelector.originalArea = geom;
                this.dispatchEvent(new CustomEvent('polygondraw', { detail: { polygon: geom } }));
                this.jobSelectorFindJobs(geom);
            }
        } catch (e) {
            console.error('jobSelectorOnDrawComplete', e);
        }
    }

    jobSelectorFindJobs(geom: any) {
        if (this.jobSelector.polygon) {
            this.map.UORemove(this.jobSelector.polygon.id);
        }
        this.jobSelector.polygon = new Targeo.MapObject.Polyline(geom.coordinates, {
            closed: true,
            visible: true,
            drawStyle: [
                {
                    strokeWeight: '2px',
                    strokeColor: '#333333',
                    strokeOpacity: '0.8',
                    fill: '#ffffff77',
                    lineDash: [5, 5],
                },
            ],
            zIndex: 600,
        });
        this.map.UOAdd(this.jobSelector.polygon);
        let endpointfunc = 'query';
        let jobsOnMapQueries = this.getFilters(PlanningOnMap.config);
        if ('query' in jobsOnMapQueries) {
            endpointfunc = 'query_in_assignment';
            jobsOnMapQueries['query']['geom'] = geom;
        } else {
            jobsOnMapQueries['geom'] = geom;
        }
        this.api
            .getData(
                'post',
                '/assignment_jobs/'+endpointfunc+'?page[limit]=-1&page[offset]=0&relations[0]=job*&projection=job.id,id,assignment_id,filter',
                jobsOnMapQueries,
                z.any(),
                z.any()
            )
            .then(
                function (this: PlanningOnMap, jobs: Array<any>) {
                    let jobIds: any = {};
                    jobs = jobs
                        .filter((j: any) => {
                            return j.filter !== false;
                        }).map((j: any) => {
                            return { jobId: j.job.id, ajId: j.id, assignmentId: j.assignment_id };
                        })
                        .filter((j: any) => {
                            if (!jobIds[j.jobId]) {
                                jobIds[j.jobId] = true;
                                if (
                                    PlanningOnMap.jobPins[j.jobId]/* &&
                                    PlanningOnMap.jobPins[j.jobId].mapObject.isOnMap()*/
                                ) {
                                    return true;
                                }
                            }
                            return false;
                        });
                    if (jobs.length) {
                        this.jobSelector.jobsInsidePolygon = jobs;
                        this.jobSetSelection(jobs.map(j => j.jobId));
                        let oneOfJobs = jobs[0].jobId;
                        let props = PlanningOnMap.jobPins[oneOfJobs];
                        this.dispatchEvent(
                            new CustomEvent('jobselect', {
                                detail: {
                                    jobId: jobs[0].jobId,
                                    ajId: props.ajId || null,
                                    assignmentId: props.assignmentId,
                                    suggestedAssignmentId: props.suggestedAssignmentId,
                                    hasSuggestion: !!props.suggestedAssignmentId,
                                    isSelected: props.selected,
                                    selection: this.jobSelection,
                                    selectionDetailed: this.jobSelection.map((j) => {
                                        return {...(
                                            ({
                                                ajId: id,
                                                assignmentId: assignment_id,
                                                suggestedAssignmentId: suggested_assignment_id
                                            }) => ({id, assignment_id, suggested_assignment_id}))(PlanningOnMap.jobPins[j] || {}), job_id: j};
                                    }),
                                    page: null,
                                },
                            })
                        );
                        this.jobSelectorMark(geom, jobs);
                    } else {
                        if (this.jobSelector.polygon) {
                            this.map.UORemove(this.jobSelector.polygon.id);
                            this.jobSelector.polygon = null;
                        }
                    }
                }.bind(this)
            )
            .catch((err: any) => {
                console.error('jobs', err);
                if (this.jobSelector.polygon) {
                    this.map.UORemove(this.jobSelector.polygon.id);
                    this.jobSelector.polygon = null;
                }
            });
    }

    jobSelectorMark(geom: any, jobs: any[]) {
        let assignments: any = {};
        get(scenarioManager)
            .getScenarioSections(false)
            .forEach(a => {
                assignments[a.getId()] = a;
            });
        let mapobjects = jobs
            .filter(j => !j.assignmentId || (!assignments[j.assignmentId].isLocked() && !assignments[j.assignmentId].hasRouteBeenEverDownloaded()))
            .map(j => PlanningOnMap.jobPins[j.jobId].mapObject);
        mapobjects.forEach(mapobj => {
            mapobj.setZIndex(602 + 90 - mapobj.coords.y);
            mapobj.setCluster(null);
        });
        if (this.jobSelector.polygon) {
            mapobjects.push(this.jobSelector.polygon);
        }
        this.map.getObjectManager().createDraggableGroup(mapobjects);
        this.jobSelector.jobs = jobs;
        this.jobSelector.mapObjects = mapobjects;
        this.jobSelector.targetAssignmentId = 0;
        this.jobSelector.listeners = [
            this.map.listen(
                'onDragGroup',
                this.jobDragOnDrag,
                false,
                this
            ),
            this.map.listen(
                'onDragGroupEnd',
                this.jobDragOnDragEnd,
                false,
                this
            ),
        ];
    }

    private jobSelectorOnKeyUp(evt: any) {
        if (evt.key == 'Control' || evt.key == 'Meta') {
            this.jobSelector.drawing = false;
            this.jobSelectorInit();
            if (this.jobSelector.coordinates && this.jobSelector.coordinates.length > 2) {
                this.jobSelectorOnDrawComplete({ coords: this.jobSelector.coordinates });
            }
        }
    }

    private jobSelectorDestroy() {
        if (this.jobSelector.polygon) {
            this.map.UORemove(this.jobSelector.polygon.id);
            this.jobSelector.polygon = null;
        }
        if (this.jobSelector.label) {
            this.map.UORemove(this.jobSelector.label.id);
            this.jobSelector.label = null;
        }
        document.body.removeEventListener('keyup', this.jobSelectorOnKeyUp);
        if (this.jobSelector.listeners) {
            this.jobSelector.listeners.forEach((l: any) => {
                this.map.unlistenByKey(l);
            });
        }
        if ((this.listeners.draw && this.listeners.draw.length) || PlanningOnMap.config.showRoute) {
            this.map.modules.Drawer.drawUnprepare();
            if (this.listeners.draw) {
                this.listeners.draw.forEach((l: any) => {
                    this.map.modules.Drawer.unlistenByKey(l);
                });
                this.listeners.draw = [];
            }
        }
        Targeo.Base.Map.modules.Controls.removeControl('job-selector');
    }

    jobDragOnDrag(e: any) {
        let assignments: any = {};
        this.map
            .getObjectManager()
            .getObjectsAtPixel({ pixel: e.page }, true)
            .filter((obj: any) => {
                if (e.objects.indexOf(obj) == -1) {
                    let assignmentId = 0;
                    let matches: string[];
                    let ispin: boolean = false;
                    if (
                        (matches = obj.id.match(/^assignment_(\d+)(_\d+)?$/)) !== null
                    ) {
                        assignmentId = parseInt(matches[1]);
                        ispin = false;
                    } else if ((matches = obj.id.match(/^job_(\d+)_\d+$/)) !== null) {
                        if (PlanningOnMap.jobPins[matches[1]]) {
                            assignmentId =
                                PlanningOnMap.jobPins[matches[1]].assignmentId ||
                                PlanningOnMap.jobPins[matches[1]].suggestedAssignmentId;
                            ispin = true;
                        }
                    }
                    if (assignmentId && !(assignmentId in assignments)) {
                        assignments[assignmentId] = ispin;
                    }
                    return true;
                }
                return false;
            });
        let assignmentId: number = 0;
        if (Object.values(assignments).filter(v => v === true).length == 1) {
            for (let a in assignments) {
                if (assignments[a] === true) {
                    assignmentId = parseInt(a);
                    break;
                }
            }
        } else if (Object.values(assignments).filter(v => v === false).length == 1) {
            for (let a in assignments) {
                if (assignments[a] === false) {
                    assignmentId = parseInt(a);
                    break;
                }
            }
        }
        if (
            assignmentId &&
            PlanningOnMap.config.selectedAssignment &&
            PlanningOnMap.config.selectedAssignment.getId() > 0 &&
            assignmentId != PlanningOnMap.config.selectedAssignment.getId()
        ) {
            assignmentId = 0;
        }
        let targetAssignment: Assignment | null = null;
        if (assignmentId) {
            targetAssignment = get(scenarioManager)
                .getScenarioSections(false)
                .filter(a => a.getId() == assignmentId)[0];
            if (
                targetAssignment.isLocked() ||
                targetAssignment.hasRouteBeenEverDownloaded()
            ) {
                assignmentId = 0;
                targetAssignment = null;
            }
        }
        if (this.jobSelector.targetAssignmentId != assignmentId) {
            this.jobSelector.targetAssignmentId = assignmentId;
            for (let i = 0, ii = e.objects.length; i < ii; i++) {
                let jobId = (e.objects[i].id.match(/^job_(\d+)_\d+$/) || [0, 0])[1];
                if (jobId) {
                    let jobData = PlanningOnMap.jobPins[jobId];
                    if (jobData) {
                        jobData.mapObject.setIcon(
                            getJobPin(
                                assignmentId
                                    ? PlanningOnMap.assignments[assignmentId].color
                                    : jobData.color,
                                jobData.filter === false
                                    ? JobsOnMap.pinSizes.unmatching
                                    : jobData.filter === true
                                        ? JobsOnMap.pinSizes.matching
                                        : JobsOnMap.pinSizes.normal,
                                {
                                    ...(assignmentId ? {} : jobData.pinOpts),
                                    type: jobData.type,
                                    checked: jobData.selected
                                },
                                get(clientSettings).display.icon_style
                            )
                        );
                    }
                } else {
                    e.objects[i].setStyle(
                        assignmentId
                            ? [
                                    {
                                        strokeWeight: '2px',
                                        strokeColor:
                                            PlanningOnMap.assignments[assignmentId].color,
                                        strokeOpacity: '0.8',
                                        fill:
                                            PlanningOnMap.assignments[assignmentId]
                                                .color + '77',
                                        lineDash: [5, 5],
                                    },
                                ]
                            : [
                                    {
                                        strokeWeight: '2px',
                                        strokeColor: '#333333',
                                        strokeOpacity: '0.8',
                                        fill: '#ffffff77',
                                        lineDash: [5, 5],
                                    },
                                ]
                    );
                }
            }
        }
        if (assignmentId) {
            if (!this.jobSelector.label) {
                this.jobSelector.labelElement = document.createElement('div');
                this.jobSelector.labelElement.style.cssText =
                    'border-radius: 3px; background-color: #fff; padding: 3px 6px; box-shadow: rgba(0, 0, 0, 0.25) 1px 1px 12px 0px';
                this.jobSelector.label = new Targeo.MapObject.DOM(
                    e.coords,
                    this.jobSelector.labelElement,
                    null,
                    {
                        interactive: false,
                        halign: 'left',
                        valign: 'top',
                        offset: { x: -20, y: -10 },
                    }
                );
                this.map.UOAdd(this.jobSelector.label);
            } else {
                this.jobSelector.label.setCoordinates(e.coords);
            }
            this.jobSelector.labelElement.innerHTML =
                get(_)('drop_jobs_to_reassign') +
                ' <b>' +
                (targetAssignment?.getVehicleName() + '</b>');
        } else if (this.jobSelector.label) {
            this.map.UORemove(this.jobSelector.label.id);
            this.jobSelector.label = null;
        }
    }

    jobDragOnDragEnd(this: PlanningOnMap, e: any) {
        if (this.jobSelector.targetAssignmentId) {
            this.jobSetSelection([]);
            this.dispatchEvent(
                new CustomEvent('jobdrag', {
                    detail: {
                        jobs: this.jobSelector.jobs,
                        target: this.jobSelector.targetAssignmentId,
                    },
                })
            );
        } else {
            if (this.jobSelector.polygon) {
                this.jobSelector.polygon.setPoints(this.jobSelector.originalArea.coordinates);
            }
            this.jobDragClean(false);
        }
    }

    jobDragClean(destroy: boolean) {
        if (this.jobSelector?.mapObjects?.length) {
            for (let i = 0, ii = this.jobSelector.mapObjects.length; i < ii; i++) {
                if (this.jobSelector.mapObjects[i].id.substring(0, 4) == 'job_') {
                    let job = PlanningOnMap.jobPins[this.jobSelector.mapObjects[i].id.match(/^job_(\d+)_\d+$/)[1]];
                    if (job) {
                        this.jobSelector.mapObjects[i].setCoordinates({
                            x: job.geom.coordinates[0],
                            y: job.geom.coordinates[1],
                        });
                        if (destroy) {
                            this.jobSelector.mapObjects[i].setCluster(
                                getJobCluster(
                                    job.color,
                                    job.filter === false
                                        ? JobsOnMap.pinSizes.unmatching
                                        : job.filter === true
                                            ? JobsOnMap.pinSizes.matching
                                            : JobsOnMap.pinSizes.normal,
                                    job.pinOpts,
                                    get(clientSettings).display.icon_style
                                )
                            );
                        }
                    }
                }
            }
            if (destroy) {
                this.jobSelector.mapObjects = null;
            }
        }
        if (this.jobSelector.label) {
            this.map.UORemove(this.jobSelector.label.id);
            this.jobSelector.label = null;
        }
        if (destroy) {
            this.map.getObjectManager().destroyDraggableGroup();
        }
        /*if (this.jobSelector.polygon) {
            this.map.UORemove(this.jobSelector.polygon.id);
            this.jobSelector.polygon = null;
        }*/
    }

    changeRoutePointsOrder(
        order: (number|null)[],
        deltas: ({ t: number; d: number; g: { t: number; d: number; m: number } } | null)[] | null
    ) {
        if (PlanningOnMap.route) {
            PlanningOnMap.route.changePointsOrder(order, deltas);
        }
    }

    setRoutePointsVisibility(visibility: boolean[]) {
        if (PlanningOnMap.route) {
            PlanningOnMap.route.changePointsVisibility(visibility);
        }
    }

    setRoute(id: string, baseId: string | null = null) {
        if (PlanningOnMap.route) {
            PlanningOnMap.route.routeSetId(id, baseId);
            PlanningOnMap.config.routeId = id;
            PlanningOnMap.config.baseRouteId = baseId;
        }
    }

    destroy(leaveAssignments: boolean = false) {
        this.loadingAssignmentsForScenario = false;
        if (!leaveAssignments) {
            this.assignmentsRemove();
            PlanningOnMap.config.scenarioId = null;
        }
        //PlanningOnMap.jobsOnMap.deactivate();
        PlanningOnMap.jobsOnMap.destroy();
        this.jobSelectorDestroy();
        this.jobsRemove();
        this.routeRemove();
        PlanningOnMap.config.assignmentQuery = null;
    }
}
